/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, Typography } from '@mui/material';
import React from 'react';
import type { InfoDialogTimeProps } from '../../store/types';
import { useDataExplorerTranslation } from '../../utils/i18n';
import { parseISO8601Duration } from '../../utils/timeUtils';

const InfoDialogTime: React.FC<InfoDialogTimeProps> = ({
  interval,
  resolution,
}: InfoDialogTimeProps) => {
  const { t } = useDataExplorerTranslation();
  // Format date range to a correct format
  const formatDateRange = (dateRange: string[]): string => {
    const formatDate = (dateString: string): string => {
      // Handle time-only format like "T00Z" or "T23Z"
      const timeMatch = /^T(\d{2})Z$/.exec(dateString);
      if (timeMatch) {
        return `${timeMatch[1]}:00 UTC`;
      }
      // Handle date format like "2024-10-31"
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        };
        return date.toLocaleDateString(undefined, options);
      }
      // Fallback for ".." or unknown
      if (dateString === '..') {
        return '';
      }
      return dateString;
    };

    if (dateRange[1] === '..') {
      return `${formatDate(dateRange[0])} -`;
    }

    return `${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}`;
  };

  return (
    <Grid
      container
      data-testid="infoDialogTime"
      spacing={2}
      sx={{ flexDirection: 'column' }}
    >
      {!!interval && interval.length > 0 && (
        <Grid container sx={{ flexDirection: 'column' }} spacing={0}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('time-information-observation-period')}
            </Typography>
          </Grid>
          <Grid>
            <Typography data-testid="infoDialogTimeObservationPeriod">
              {formatDateRange(interval)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {!!resolution && (
        <Grid container sx={{ flexDirection: 'column' }} spacing={0}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('time-information-data-resolution')}
            </Typography>
          </Grid>
          <Grid>
            <Typography data-testid="infoDialogTimeResolution">
              {t('Every ')}
              {parseISO8601Duration(resolution)}
            </Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default InfoDialogTime;
